import React, { useState, useMemo, useRef, useEffect, forwardRef } from "react";
import { useSecurity, useUserInfo } from "../utils/security";

import { Box, Typography, TextField, Button, IconButton, Avatar, InputBase } from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";

import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import AlternateEmailOutlinedIcon from "@mui/icons-material/AlternateEmailOutlined";
import PasswordOutlinedIcon from "@mui/icons-material/PasswordOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import CloseIcon from "@mui/icons-material/Close";
import SettingsIcon from "@mui/icons-material/SettingsOutlined";
import DoneIcon from "@mui/icons-material/Done";
import LoadingButton from "@mui/lab/LoadingButton";

import { avatarString } from "../utils/strings";
import { useNavigate } from "react-router-dom";
import { wait } from "../utils/mock";
import { useForm } from "../utils/form";

export const PasswordField = ({ readOnly, disabled, ...props }) => {
    const [showPassword, setShowPassword] = useState(false);

    return (
        <TextField
            {...props}
            disabled={disabled}
            type={showPassword && !disabled ? "text" : "password"}
            InputProps={{
                readOnly,
                endAdornment: (
                    <IconButton disabled={disabled} onClick={() => setShowPassword(!showPassword)}>
                        {showPassword && !disabled ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                ),
            }}
        />
    );
};

export const PasswordFieldMain = ({ readOnly, disabled, ...props }) => {
    const [showPassword, setShowPassword] = useState(false);

    return (
        <Box
            sx={(theme) => ({
                display: "flex",
                flexDirection: "row",
                gap: theme.spacing(2),
                alignItems: "center",
                borderRadius: theme.spacing(3),
                paddingTop: theme.spacing(0.5),
                paddingRight: theme.spacing(1),
                paddingBottom: theme.spacing(0.5),
                paddingLeft: theme.spacing(1),
                backgroundColor: theme.palette.surfaceDim.main,
                marginBottom: theme.spacing(4),
            })}>
            <PasswordOutlinedIcon />
            <InputBase
                placeholder="Heslo"
                sx={{ flex: 1 }}
                disabled={disabled}
                type={showPassword && !disabled ? "text" : "password"}
            />
            <IconButton disabled={disabled} onClick={() => setShowPassword(!showPassword)}>
                {showPassword && !disabled ? <Visibility /> : <VisibilityOff />}
            </IconButton>
        </Box>
    );
};

const UsernameFieldMain = forwardRef(({ ...props }, ref) => (
    <Box
        sx={(theme) => ({
            display: "flex",
            flexDirection: "row",
            gap: theme.spacing(2),
            alignItems: "center",
            borderRadius: theme.spacing(3),
            paddingTop: theme.spacing(0.5),
            paddingRight: theme.spacing(1),
            paddingBottom: theme.spacing(0.5),
            paddingLeft: theme.spacing(1),
            backgroundColor: theme.palette.surfaceDim.main,
            marginBottom: theme.spacing(4),
            height: theme.spacing(5),
        })}>
        <AlternateEmailOutlinedIcon />
        <InputBase sx={{ flex: 1 }} placeholder="Uživatelské jméno" {...props} inputRef={ref} />
    </Box>
));

export const UserInfoIconButton = ({ onClick }) => {
    const { userInfo } = useUserInfo();
    return (
        <IconButton onClick={onClick}>
            <Avatar
                sx={(theme) => ({
                    width: 24,
                    height: 24,
                    fontSize: 12,
                    backgroundColor: (theme.palette.tertiary || theme.palette.secondary).main,
                    color: (theme.palette.tertiary || theme.palette.secondary).contrastText,
                })}>
                {avatarString(userInfo.name)}
            </Avatar>
        </IconButton>
    );
};

export const UserInfoBanner = () => {
    const { userInfo } = useUserInfo();
    return (
        <>
            <Box
                sx={(theme) => ({
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: theme.spacing(2),
                })}>
                <Box sx={{ flex: "0 0 auto" }}>
                    <Avatar sx={{ width: 40, height: 40, fontSize: 20 }}>
                        {avatarString(userInfo.name)}
                    </Avatar>
                </Box>
                <Box
                    sx={(theme) => ({
                        flex: "1 1 auto",
                        display: "flex",
                        flexDirection: "column",
                        justifyItems: "center",
                        padding: `0px ${theme.spacing(1)}`,
                    })}>
                    <Typography variant="caption" component="div">
                        <b>{userInfo.name}</b>
                    </Typography>
                    <Typography variant="caption" component="div">
                        {userInfo.email}
                    </Typography>
                    <Typography variant="caption" component="div">
                        {!!userInfo.owner ? "správce" : "pracovník"}
                    </Typography>
                </Box>
            </Box>
        </>
    );
};

const ActionButton = ({ children, loading, success, ...props }) => {
    const sx = (theme) => {
        let sx = { ...(props.sx || {}) };
        if (success) {
            sx.backgroundColor = theme.palette.success.main;
            sx.color = theme.palette.onSuccess.main;
        }
        return sx;
    };
    return (
        <LoadingButton {...props} loading={loading} sx={sx}>
            <span style={{ visibility: success ? "hidden" : "visible" }}>{children}</span>
            {!!success && (
                <span style={{ position: "absolute", lineHeight: "0.5rem" }}>
                    <DoneIcon />
                </span>
            )}
        </LoadingButton>
    );
};

export const useUserMenu = () => {
    const navigate = useNavigate();
    const { logout } = useSecurity();
    return useMemo(
        () => ({
            title: "Informace o uživateli",
            render: ({ onClick, disabled }) => (
                <UserInfoIconButton onClick={onClick} disabled={disabled} />
            ),
            items: [
                {
                    render: () => <UserInfoBanner />,
                },
                {
                    title: "Nastavení",
                    icon: <SettingsIcon />,
                    onClick: () => navigate("/settings"),
                },
                {
                    title: "Odhlásit se",
                    icon: <LogoutIcon />,
                    onClick: () =>
                        logout(() => {
                            navigate("/");
                        }),
                },
            ],
        }),
        [logout, navigate]
    );
};

const SecurityPageLayout = ({ title, children, onClose }) => {
    return (
        <Box
            sx={{
                flex: "1 1 auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: 0,
                overflowX: "hidden",
                overflowY: "auto",
            }}>
            <Box
                sx={(theme) => ({
                    //border: "1px solid red",
                    width: 500,
                    maxWidth: "80%",
                    padding: theme.spacing(3),
                })}>
                <Typography
                    color="inherit"
                    sx={(theme) => ({
                        fontWeight: 500,
                        letterSpacing: 0.5,
                        fontSize: 20,
                        opacity: 0.8,
                        marginBottom: theme.spacing(4),
                    })}>
                    {title}
                </Typography>
                {children}
            </Box>
            {!!onClose && (
                <IconButton
                    sx={(theme) => ({
                        position: "absolute",
                        right: theme.spacing(2),
                        top: theme.spacing(2),
                    })}
                    onClick={onClose}
                    title="Domů">
                    <CloseIcon />
                </IconButton>
            )}
        </Box>
    );
};

export const LoginPage = ({ onLogin }) => {
    const navigate = useNavigate();
    const { login } = useSecurity();
    const onSubmit = async (data) => {
        try {
            console.log(data);
            setLoading(true);
            setSuccess(false);
            await login(data.login, data.password, async () => {
                setSuccess(true);
                setLoading(false);
                await wait(1500);
            });
        } finally {
            setLoading(false);
            setSuccess(false);
        }
    };
    const { values, handleChange, handleSubmit } = useForm({ onSubmit });

    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    const usernameRef = useRef(null); // Create a ref
    useEffect(() => {
        if (usernameRef.current) {
            usernameRef.current.focus(); // Focus the field when the component mounts
        }
    }, []);

    return (
        <SecurityPageLayout title="Vítejte zpět" onClose={() => navigate("/")}>
            <Box sx={{ overflow: "hidden" }}>
                <form onSubmit={handleSubmit} noValidate>
                    <UsernameFieldMain
                        onChange={handleChange}
                        value={values.username}
                        name="username"
                        disabled={loading || success}
                        ref={usernameRef}
                    />
                    <PasswordFieldMain
                        onChange={handleChange}
                        value={values.password}
                        name="password"
                        disabled={loading || success}
                    />
                    <Box
                        sx={(theme) => ({
                            display: "flex",
                            gap: theme.spacing(3),
                            alignItems: "center",
                            justifyContent: "space-between",
                        })}>
                        <Button
                            onClick={(e) => {
                                e.preventDefault();
                            }}
                            disabled={loading || success}>
                            Zapomenuté heslo
                        </Button>
                        <ActionButton
                            type="submit"
                            size="large"
                            variant="contained"
                            loading={loading}
                            success={success}>
                            Přihlásit
                        </ActionButton>
                    </Box>
                </form>
            </Box>
        </SecurityPageLayout>
    );
};

export const SignupPage = ({ onSignup }) => {
    const navigate = useNavigate();
    const { signup } = useSecurity();
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    const onSubmit = async (data) => {
        try {
            setLoading(true);
            setSuccess(false);
            await signup(values.login, values.password, async () => {
                setSuccess(true);
                setLoading(false);
                await wait(1500);
            });
        } finally {
            setLoading(false);
            setSuccess(false);
        }
    };
    const { values, handleChange, handleSubmit } = useForm({ onSubmit });

    const usernameRef = useRef(null); // Create a ref
    useEffect(() => {
        if (usernameRef.current) {
            usernameRef.current.focus(); // Focus the field when the component mounts
        }
    }, []);

    return (
        <SecurityPageLayout title="Vytvoření účtu" onClose={() => navigate("/")}>
            <Box>
                <form onSubmit={handleSubmit} noValidate>
                    <UsernameFieldMain
                        value={values.username}
                        onChange={handleChange}
                        name="username"
                        disabled={loading || success}
                        ref={usernameRef}
                    />
                    <PasswordFieldMain
                        value={values.password}
                        onChange={handleChange}
                        name="password"
                        disabled={loading || success}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={!!values.privacy}
                                onChange={handleChange}
                                name="privacy"
                            />
                        }
                        label="Souhlasím s podmínkami služby a zásadami ochrany soukromí"
                        disabled={loading || success}
                    />
                    <Box
                        sx={(theme) => ({
                            display: "flex",
                            gap: theme.spacing(3),
                            alignItems: "center",
                            justifyContent: "flex-end",
                            marginTop: theme.spacing(4),
                        })}>
                        <ActionButton
                            size="large"
                            variant="contained"
                            type="submit"
                            loading={loading}
                            success={success}>
                            Pokračovat
                        </ActionButton>
                    </Box>
                </form>
            </Box>
        </SecurityPageLayout>
    );
};

export const RequestPasswordChangePage = () => {
    return <></>;
};

export const ChangePasswordPage = () => {
    return <></>;
};
